import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BuildingConstruction,
  BuildingConstructionRow,
} from '../../../../../../services/types/Calculator';
import { Button, ConfirmModal, DropDown, Modal } from '../../../../../../shared/components';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import BasicRadioButton from '../../../../../../shared/components/BasicRadioButton';
import CalculatorSectorContext from '../UrbanInfrastructureTool/CalculatorSectorContext';
import {
  setBuildingNotEstimatedEmissions,
  calculateBuildingNotEstimatedEmissions,
  setBuildingEstimatedEmissions,
} from '../../Utils/moduleCalculates';
import BuildingConstructionDetails from './Details/BuildingConstructionDetails';
import MainContext from '../../../../../App/MainProvider/context';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';

import CalculatorTableWrapper from '../Shared/CalculatorTableWrapper';

interface Props {
  buildingConstruction: BuildingConstruction;
  onUpdateBuildingConstruction: (updatedBuildingConstruction: BuildingConstruction) => void;
}

const BuildingConstructionSection: React.FC<Props> = ({
  buildingConstruction,
  onUpdateBuildingConstruction,
}) => {
  const { t } = useTranslation();
  const { notify } = useContext(MainContext);

  const [selectedValueBuildingConstructionOption, setSelectedValue] = useState<number | null>(0);
  const [buildingInputValue, setBuildingInputValue] = useState<string>('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(
    buildingConstruction.subTotal
  );
  const [rows, setRows] = useState<BuildingConstructionRow[]>(buildingConstruction.rows || []);
  const initialRadio =
    buildingConstruction.isEstimated !== null ? Number(buildingConstruction.isEstimated) : null;
  const [radioSelected, setRadioSelected] = useState<number | null>(initialRadio);
  const { measurementSystem } = useContext(CalculatorSectorContext);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [rowForDelete, setRowForDelete] = useState<number | null>(null);

  const isAddButtonDisabled =
    selectedValueBuildingConstructionOption === null ||
    selectedValueBuildingConstructionOption === 0 ||
    Number(buildingInputValue) <= 0;

  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(initialRadio);

  const buildingConstructionOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(buildingConstruction.buildingsType
      ? buildingConstruction.buildingsType.map((x) => ({
          id: x.id,
          text: x.name,
          value: x.id.toString(),
        }))
      : []),
  ];

  const handleRadioChange = (value: number) => {
    setRadioSelected(value);

    onUpdateBuildingConstruction({
      ...buildingConstruction,
      isEstimated: value === 1,
      totalArea: null,
      moduleId: buildingConstruction.moduleId,
      subTotal: null,
    });

    setTotalCarbonSum(null);

    setExpandedRowIndex(value);
  };

  const getTextMeasure = (): string => (measurementSystem.id === 1 ? 'Meters' : 'Feet');

  const handleChange = (value: number) => {
    setSelectedValue(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setBuildingInputValue(value);
    }
  };

  const handleInputTotalArea = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return; // Si excede 10 caracteres, no actualiza el estado
    }
    // Permitir valores vacíos
    onUpdateBuildingConstruction({
      ...buildingConstruction,
      totalArea: value === '' ? null : Number(value),
    });
  };

  const isDuplicateBuildingsType = (existingRows: BuildingConstructionRow[], coverId: number) => {
    const isDuplicate = existingRows.some((row) => row.buildingTypeId === coverId);

    if (isDuplicate) {
      notify(
        'Building Construction',
        'A row with this Building Type already exists.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  const handleAddRow = () => {
    if (selectedValueBuildingConstructionOption !== null && Number(buildingInputValue) > 0) {
      const selectedBuildingsType = buildingConstruction.buildingsType.find(
        (x) => x.id === selectedValueBuildingConstructionOption
      );

      if (selectedBuildingsType) {
        if (isDuplicateBuildingsType(rows, selectedBuildingsType.id)) {
          return;
        }

        const updateBuilding = setBuildingEstimatedEmissions(
          buildingConstruction,
          selectedBuildingsType.averageSqFt * Number(buildingInputValue),
          measurementSystem.id
        );

        const subTotal = calculateBuildingNotEstimatedEmissions(updateBuilding);

        const newRow = {
          id: 0,
          buildingTypeId: selectedBuildingsType.id,
          buildingTypeName: selectedBuildingsType.name,
          numberBuildings: Number(buildingInputValue),
          defaultArea: selectedBuildingsType.averageSqFt,
          totalCarbon: subTotal,
        };

        setRows((prevRows) => {
          const updatedRows = [...prevRows, newRow];
          const newTotalCarbonSum = subTotal;
          onUpdateBuildingConstruction({
            ...buildingConstruction,
            rows: updatedRows,
            subTotal,
          });
          setTotalCarbonSum(newTotalCarbonSum);
          return updatedRows;
        });

        setBuildingInputValue('');
        setSelectedValue(0);
      }
    }
  };

  const confirmRemoveRow = () => {
    if (rowForDelete !== null) {
      setRows((prevRows) => {
        const updatedRows = prevRows.filter((_, i) => i !== rowForDelete);

        onUpdateBuildingConstruction({
          ...buildingConstruction,
          rows: updatedRows,
        });
        return updatedRows;
      });
      setShowConfirmDelete(false);
      setRowForDelete(null);
    }
  };

  const handleRemoveRow = (index: number) => {
    setRowForDelete(index);
    setShowConfirmDelete(true);
  };

  useEffect(() => {
    const { isEstimated } = buildingConstruction;
    const totalArea = Number(buildingConstruction.totalArea);

    if (isEstimated === null) {
      setTotalCarbonSum(null);
      onUpdateBuildingConstruction({
        ...buildingConstruction,
        isEstimated: null,
        totalArea: null,
        moduleId: buildingConstruction.moduleId,
        subTotal: null,
      });
    } else if (!isEstimated && totalArea && buildingConstruction.totalArea) {
      const updatedConstruction = setBuildingNotEstimatedEmissions(
        buildingConstruction,
        measurementSystem.id
      );

      const result = calculateBuildingNotEstimatedEmissions(updatedConstruction);

      onUpdateBuildingConstruction({
        ...updatedConstruction,
        totalArea,
        isEstimated: false,

        moduleId: 0,
        subTotal: result,
      });
      setTotalCarbonSum(result);
    }
    if (isEstimated) {
      if (buildingConstruction.rows && buildingConstruction.rows.length > 0) {
        const estimatedTotalArea = buildingConstruction.rows.reduce(
          (sum, row) => sum + row.defaultArea * row.numberBuildings,
          0
        );
        const newBuilding = setBuildingEstimatedEmissions(
          buildingConstruction,
          estimatedTotalArea,
          measurementSystem.id
        );

        const estimatedCarbonSum = calculateBuildingNotEstimatedEmissions(newBuilding);

        newBuilding.subTotal = estimatedCarbonSum;
        onUpdateBuildingConstruction(newBuilding);
        setTotalCarbonSum(estimatedCarbonSum);
      } else {
        setTotalCarbonSum(null);
        onUpdateBuildingConstruction({ ...buildingConstruction, subTotal: null });
      }
    }

    if (buildingConstruction.totalArea === null && !isEstimated) {
      onUpdateBuildingConstruction({ ...buildingConstruction, subTotal: null });
      setTotalCarbonSum(null);
    }
  }, [buildingConstruction.isEstimated, buildingConstruction.totalArea, buildingConstruction.rows]);

  return (
    <>
      <div className="row calculator-title-row">
        <div className="radio-calculator">
          <BasicRadioButton
            id="rb_building_are"
            name="building"
            label="Enter the total area of constructed space for all buildings in the project, if known."
            radioValue={0}
            active={radioSelected === 0}
            onChange={(value) => handleRadioChange(value)}
          />
          <BasicRadioButton
            id="rb_building_notknow"
            name="building"
            label="If the total area of constructed space in the project is not known, enter the number of buildings and a default average area per building will be used."
            radioValue={1}
            active={radioSelected === 1}
            onChange={(value) => handleRadioChange(value)}
          />
        </div>
      </div>
      {expandedRowIndex === 0 && (
        <>
          <br />
          <div className="row calculator-input-row">
            <div className="col-12 col-sm-5 col-md-3 col-lg-2">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Square ') + getTextMeasure()}
              </label>
            </div>
            <div className="col-12 col-sm-5 col-md-4 col-lg-2">
              <input
                title="Square Meters"
                type="number"
                id="totalAreaInput"
                value={
                  buildingConstruction.totalArea !== null ? buildingConstruction.totalArea : ''
                }
                onChange={handleInputTotalArea}
                min="0"
                max={99999999.99}
                maxLength={10}
                onKeyDown={handleKeyDown}
                className="calculator-input"
              />
            </div>
          </div>
        </>
      )}

      {expandedRowIndex === 1 && (
        <>
          <h4 className="table-calculator-title">{t('Select Parameters')}</h4>
          <div className="form-row align-items-center calculator-forms table-wrapper-calc">
            <div className="col-sm-2 d-flex align-items-center">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Building Type')}
              </label>
            </div>
            <div className="col-sm-3 d-flex align-items-center">
              <DropDown
                elements={buildingConstructionOptions}
                value={selectedValueBuildingConstructionOption}
                onChange={handleChange}
                key="drop-landUseChange"
                className="dropdown-module-calculator"
              />
            </div>
            <div className="col-sm-2 d-flex align-items-center">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Number of Buildings')}
              </label>
            </div>

            <div className="col-sm-2 d-flex align-items-center">
              <input
                title="Number Of Buildings"
                type="number"
                id="landUseInput"
                value={buildingInputValue}
                onChange={handleInputChange}
                min="0"
                max={99999999.99}
                maxLength={10}
                onKeyDown={handleKeyDown}
                className="calculator-input"
              />
            </div>
            <div className="col-sm-3">
              <Button
                htmlId="addRowLandUse"
                className="btn btn-primary px-4 py-1 calculator-add-button"
                style={{ float: 'right' }}
                text={t('Add Row')}
                onClick={handleAddRow}
                disabled={isAddButtonDisabled}
              />
            </div>
          </div>
        </>
      )}

      <CalculatorTableWrapper
        totalCarbonSum={totalCarbonSum}
        onShowDetailsModal={() => setShowDetailsModal(true)}
        subtotalColSpan={4}
        colGroup={
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
        }
      >
        {buildingConstruction.isEstimated && (
          <thead>
            <tr>
              <th>Building Type</th>
              <th>Number of Buildings</th>
              <th>{t('Square ') + getTextMeasure()}</th>
              <th className="text-right">Subtotal</th>
              <th className="action-column text-right">{t('actions')}</th>
            </tr>
          </thead>
        )}
        <tbody>
          {buildingConstruction.isEstimated &&
            buildingConstruction.rows &&
            buildingConstruction.rows.length > 0 &&
            rows.map((row, index) => (
              <tr key={row.id}>
                <td>{row.buildingTypeName}</td>
                <td>{row.numberBuildings}</td>
                <td>
                  <NumberFormatterSinSeparador value={row.defaultArea} />
                </td>
                <td className="text-right">
                  <NumberFormatterSinSeparador value={row.totalCarbon} /> t CO<sub>2</sub>
                </td>
                <td className="action-column text-right p11px">
                  <Button
                    htmlId={`remove-row-landuse-${index}`}
                    className="btn-tertiary no-padding action-calculator"
                    icon="trash-alt"
                    text=""
                    onClick={() => handleRemoveRow(index)}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </CalculatorTableWrapper>
      {showConfirmDelete && rowForDelete !== null && (
        <ConfirmModal
          visible={showConfirmDelete}
          buttonText={t('Confirm')}
          setVisible={setShowConfirmDelete}
          action={confirmRemoveRow}
        >
          {t('confirmDeleteCalculateMessage')}
        </ConfirmModal>
      )}
      <Modal
        title={buildingConstruction.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <BuildingConstructionDetails buildingConstruction={buildingConstruction} />
        </div>
      </Modal>
    </>
  );
};

export default BuildingConstructionSection;
